"use client";

import { useState, useTransition, useCallback, useRef, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import {
  ChevronDown,
  Clock,
  Filter,
  Loader2,
  Search,
  X,
} from "lucide-react";
import { cn, formatDate } from "@/lib/format";
import { resolveMediaUrl } from "@/lib/media";
import { pathForSync } from "@/lib/routing-shared";
import type { BlogList, Category, Pagination } from "@/lib/dto";

type Props = {
  initialItems: BlogList[];
  initialPagination: Pagination;
  categories: Category[];
  years: number[];
  /**
   * When set, the list is bound to this category slug: it's the initial
   * filter, it can't be cleared client-side, and the category pills become
   * links to each category's own `/[slug]/` page instead of client toggles.
   * Used by the per-category route (`BlogCategoryDetailPage`).
   */
  lockedCategory?: string;
  /**
   * `"index"` (default) — the full blog index: "Filter" heading, search box,
   * category + year pills. `"detail"` — a leaner list for a detail page
   * (category page): drops the search box and "Filter" heading, keeps the
   * category cross-nav + year pills + grid + load-more.
   */
  variant?: "index" | "detail";
};

// ─── Card ────────────────────────────────────────────────────────────────────

/**
 * Client-side twin of `content/BlogCard`. Identical markup, but resolves the
 * blog URL with the sync `pathForSync` (blogs live at the root for this
 * tenant) — the async server `BlogCard` cannot be rendered from this Client
 * Component, and this list re-renders on the client on every filter / "load
 * more". Keep the two in visual sync.
 */
function BlogCardClient({ blog }: { blog: BlogList }) {
  const img = blog.featured_image?.url
    ? resolveMediaUrl(blog.featured_image.url)
    : null;
  const href = pathForSync("blogs", blog.slug);

  return (
    <div className="group overflow-hidden item rounded transition-all">
      <figure>
        <Link
          href={href}
          aria-label={blog.title}
          className="relative block image-slot aspect-[450/350] overflow-hidden rounded bg-[color:var(--color-bg)]"
        >
          {img ? (
            <Image
              src={img}
              alt={blog.featured_image?.alt || blog.title}
              fill
              sizes="(min-width: 768px) 25vw, 50vw"
              className="object-cover transition-transform group-hover:scale-110"
            />
          ) : null}
        </Link>
      </figure>
      <figcaption className="px-1 pt-3">
        {blog.published_at ? (
          <ul className="flex blog-meta flex-wrap gap-x-3 pb-1 text-sm text-muted">
            <li className="inline-flex items-center gap-x-1">
              <Clock size={14} className="text-primary" aria-hidden />
              {formatDate(blog.published_at)}
            </li>
          </ul>
        ) : null}
        <h3 className="line-clamp-2 text-base leading-[1.25] md:text-xl">
          <Link href={href} className="text-headings hover:text-primary">
            {blog.title}
          </Link>
        </h3>
      </figcaption>
    </div>
  );
}

// ─── Filter pill ─────────────────────────────────────────────────────────────

const PILL_CLASS =
  "rounded-full px-3.5 py-1 text-[13px] font-medium transition-all duration-150 border whitespace-nowrap";
const pillTone = (active: boolean) =>
  active
    ? "bg-primary text-white border-primary shadow-sm"
    : "bg-transparent border-line text-muted hover:border-primary/50 hover:text-primary";

function Pill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(PILL_CLASS, pillTone(active))}
    >
      {children}
    </button>
  );
}

function PillLink({
  href,
  active,
  children,
}: {
  href: string;
  active: boolean;
  children: React.ReactNode;
}) {
  return (
    <Link
      href={href}
      aria-current={active ? "page" : undefined}
      className={cn(PILL_CLASS, pillTone(active))}
    >
      {children}
    </Link>
  );
}

// ─── Main ────────────────────────────────────────────────────────────────────

export function BlogListClient({
  initialItems,
  initialPagination,
  categories,
  years,
  lockedCategory,
  variant = "index",
}: Props) {
  const showSearch = variant === "index";
  const [activeCategory, setActiveCategory] = useState<string | null>(
    lockedCategory ?? null,
  );
  const [activeYear, setActiveYear] = useState<number | null>(null);
  const [search, setSearch] = useState("");
  const [items, setItems] = useState<BlogList[]>(initialItems);
  const [pagination, setPagination] = useState<Pagination>(initialPagination);
  const [isFiltering, setIsFiltering] = useState(false);
  const [isPending, startTransition] = useTransition();
  const searchRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const buildUrl = useCallback(
    (page: number, cat: string | null, year: number | null, q: string) => {
      const p = new URLSearchParams({ page: String(page), limit: "10" });
      if (cat) p.set("category", cat);
      if (year) p.set("year", String(year));
      if (q.trim()) p.set("search", q.trim());
      return `/api/blogs?${p}`;
    },
    [],
  );

  const fetchPage = useCallback(
    (
      page: number,
      cat: string | null,
      year: number | null,
      q: string,
      append = false,
    ) => {
      if (!append) setIsFiltering(true);
      startTransition(async () => {
        const res = await fetch(buildUrl(page, cat, year, q));
        const data = await res.json();
        if (append) {
          setItems((prev) => [...prev, ...data.items]);
        } else {
          setItems(data.items);
        }
        setPagination(data.pagination);
        if (!append) setIsFiltering(false);
      });
    },
    [buildUrl],
  );

  function handleCategory(slug: string | null) {
    setActiveCategory(slug);
    fetchPage(1, slug, activeYear, search);
  }

  function handleYear(year: number | null) {
    setActiveYear(year);
    fetchPage(1, activeCategory, year, search);
  }

  function handleSearchChange(value: string) {
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchPage(1, activeCategory, activeYear, value);
    }, 400);
  }

  function clearSearch() {
    setSearch("");
    searchRef.current?.focus();
    fetchPage(1, activeCategory, activeYear, "");
  }

  function loadMore() {
    fetchPage(pagination.page + 1, activeCategory, activeYear, search, true);
  }

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  // Reset every filter — but never the locked category on a per-category page.
  function clearAll() {
    if (!lockedCategory) setActiveCategory(null);
    setActiveYear(null);
    setSearch("");
    fetchPage(1, lockedCategory ?? null, null, "");
  }

  const loading = isFiltering || isPending;
  // On a locked page the category is baseline state, not a user "filter".
  const hasFilters =
    (!lockedCategory && !!activeCategory) || !!activeYear || !!search.trim();

  return (
    <div className="space-y-8">
      {/* ── Filter bar ─────────────────────────────────────────────────── */}

      {showSearch ? (
        <>
          <label
            htmlFor="search"
            className="flex text-headings items-center mb-3 font-bold font-display gap-x-1.5"
          >
            <Filter aria-hidden size={16} className="text-primary" />
            Filter
          </label>

          <div className="relative">
            <Search
              size={15}
              className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-muted-soft"
              aria-hidden
            />
            <input
              ref={searchRef}
              type="search"
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Search stories…"
              aria-label="Search blog posts"
              className="h-11 w-full min-w-[300px] max-w-xl bg-white shadow-xs border border-headings/20 rounded-full pl-10 pr-10 text-sm text-headings placeholder:text-muted-soft transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15"
            />
            {search && (
              <button
                type="button"
                onClick={clearSearch}
                aria-label="Clear search"
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-muted-soft hover:text-headings transition-colors"
              >
                <X size={14} />
              </button>
            )}
          </div>
        </>
      ) : null}

      <div className="flex flex-wrap items-center gap-3">
        {/* Category row. On a locked (per-category) page the pills are links to
            each category's own `/[slug]/` page — crawlable, and each is a real
            navigation, not a client toggle. "All" goes to the blog index. */}
        {categories.length > 0 && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <span className="text-[11px] font-semibold uppercase tracking-widest text-muted-soft shrink-0">
              By Category:
            </span>
            <div className="flex flex-wrap gap-1.5">
              {lockedCategory ? (
                <>
                  {/* "All" → the blog index (a CMS `blog-list-page` article at
                      /blog/, not derivable from the routing prefixes). */}
                  <PillLink href="/blog/" active={false}>
                    All
                  </PillLink>
                  {categories.map((c) => (
                    <PillLink
                      key={c.id}
                      href={pathForSync("blog_category", c.slug)}
                      active={lockedCategory === c.slug}
                    >
                      {c.name}
                    </PillLink>
                  ))}
                </>
              ) : (
                <>
                  <Pill active={!activeCategory} onClick={() => handleCategory(null)}>
                    All
                  </Pill>
                  {categories.map((c) => (
                    <Pill
                      key={c.id}
                      active={activeCategory === c.slug}
                      onClick={() =>
                        handleCategory(activeCategory === c.slug ? null : c.slug)
                      }
                    >
                      {c.name}
                    </Pill>
                  ))}
                </>
              )}
            </div>
          </div>
        )}

        {/* Year row */}
        {years.length > 2 && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <span className="text-[11px] font-semibold uppercase tracking-widest text-muted-soft w-16 shrink-0">
              Year
            </span>
            <div className="flex flex-wrap gap-1.5">
              <Pill active={!activeYear} onClick={() => handleYear(null)}>
                All
              </Pill>
              {years.map((y) => (
                <Pill
                  key={y}
                  active={activeYear === y}
                  onClick={() => handleYear(activeYear === y ? null : y)}
                >
                  {y}
                </Pill>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Search */}

      {/* Active filter summary */}
      {hasFilters && (
        <div className="flex items-center justify-between border-t border-line pt-4">
          <p className="text-xs text-muted">
            <span className="font-semibold text-headings">
              {pagination.total}
            </span>{" "}
            result{pagination.total !== 1 ? "s" : ""}
            {activeCategory
              ? ` · ${categories.find((c) => c.slug === activeCategory)?.name}`
              : ""}
            {activeYear ? ` · ${activeYear}` : ""}
            {search.trim() ? ` · "${search.trim()}"` : ""}
          </p>
          <button
            type="button"
            onClick={clearAll}
            className="text-xs font-semibold text-primary hover:underline"
          >
            Clear all
          </button>
        </div>
      )}

      {/* ── Content area ───────────────────────────────────────────────── */}
      <div className="relative min-h-[300px]">
        {/* Loading pill */}
        {loading && (
          <div className="absolute inset-x-0 top-6 z-20 flex justify-center">
            <div className="flex items-center gap-2 rounded-full border border-line bg-white px-5 py-2 shadow-md">
              <Loader2
                size={14}
                className="animate-spin text-primary"
                aria-hidden
              />
              <span className="text-sm font-medium text-headings">
                Loading…
              </span>
            </div>
          </div>
        )}

        <div
          className={cn(
            "transition-opacity duration-200",
            loading && "opacity-30 pointer-events-none",
          )}
        >
          {items.length > 0 ? (
            <>
              {/* Featured hero — only without active filters */}
              {/* {!hasFilters && featured && (
                <div className="mb-8">
                  <FeaturedCard blog={featured} />
                </div>
              )} */}

              {/* Grid — BlogCardClient (sync `pathForSync`) is used instead of
                  the async server `BlogCard`: this list re-renders on the
                  client on every filter / "load more", and an async component
                  cannot be rendered from a Client Component. */}
              <ul className="grid gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
                {items.map((blog) => (
                  <li key={blog.id}>
                    <BlogCardClient blog={blog} />
                  </li>
                ))}
              </ul>
            </>
          ) : (
            !loading && (
              <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-line py-24 text-center">
                <div className="mb-4 rounded-full bg-primary/8 p-4">
                  <Search size={22} className="text-primary" aria-hidden />
                </div>
                <p className="font-display text-xl text-headings">
                  No stories found
                </p>
                <p className="mt-1.5 text-sm text-muted max-w-[26ch]">
                  Try a different keyword, category, or year.
                </p>
                <button
                  type="button"
                  onClick={clearAll}
                  className="mt-5 rounded-full border border-primary/30 bg-primary/5 px-5 py-2 text-sm font-medium text-primary hover:bg-primary/10 transition-colors"
                >
                  Clear filters
                </button>
              </div>
            )
          )}
        </div>
      </div>

      {/* ── Load more ──────────────────────────────────────────────────── */}
      {pagination.has_next && !loading && (
        <div className="flex flex-col items-center gap-2 pt-4">
          <button
            type="button"
            onClick={loadMore}
            disabled={isPending}
            className="inline-flex items-center gap-2.5 rounded-full border border-line bg-white px-9 py-3 text-sm font-semibold text-headings shadow-sm transition-all hover:border-primary/40 hover:bg-primary/5 hover:text-primary hover:shadow-md disabled:opacity-50"
          >
            {isPending ? (
              <Loader2 size={15} className="animate-spin" aria-hidden />
            ) : (
              <ChevronDown size={15} aria-hidden />
            )}
            Load more stories
          </button>
          <p className="text-xs text-muted-soft">
            Showing {items.length} of {pagination.total}
          </p>
        </div>
      )}
    </div>
  );
}
